/**
 * Live status + log stream for one runbook run.
 *
 * @module features/runbooks/useRunbookRunStream
 * @description Subscribes to `GET /jobs/{runId}/stream` with the RUN id (a
 * run's job id is never handed back to the client — see
 * `packages/types/src/runbook-run-events.ts`) and folds the events into a
 * render-ready shape: the latest status and the concatenated log.
 *
 * Mirrors `useExternalRunStream` almost verbatim (pure reducer + EventSource +
 * `since` replay cursor + capped exponential backoff reconnect), adapted for a
 * run that has one status line and one growing log instead of many shards.
 *
 * **Not the source of truth once the run is terminal.** The stream's log is
 * built from deltas and only covers what arrived while a subscriber was
 * connected — a page reload starts empty. `GET /org/runbook-runs/:id`'s
 * `logUrl` (the flushed, complete log) is authoritative once the run reaches a
 * terminal status; the stream exists for the "what is happening right now"
 * window before that.
 */
import React from 'react';
import {
  RUNBOOK_RUN_EVENT_TYPES,
  type RunbookRunEvent,
  type RunbookRunStreamStatus,
} from '@chatsdet/types';
import { API_URL } from '../../config';

const MAX_RECONNECT_ATTEMPTS = 5;

export interface RunbookRunStreamState {
  /** Latest lifecycle status seen on the stream; null until one arrives. */
  status: RunbookRunStreamStatus | null;
  exitCode: number | null | undefined;
  /** OUR failure reason, set only when the run never got far enough to produce script output. */
  error: string | null | undefined;
  /** Concatenated stdout+stderr deltas, in arrival order. */
  log: string;
  /**
   * Highest log `seq` already folded in, or -1. The stream replays cached
   * events with `emittedAt >= since`, so a reconnect re-delivers the event at
   * the cursor — idempotent for status, but a delta APPENDS, so without this
   * every reconnect would duplicate a chunk of the log.
   */
  lastLogSeq: number;
  /**
   * True once a delta arrived out of sequence, meaning the live log is missing
   * a stretch the broker's bounded cache evicted. Surfaced so the viewer can
   * say the live output is partial instead of quietly implying it is whole.
   */
  hasGap: boolean;
}

const EMPTY: RunbookRunStreamState = {
  status: null,
  exitCode: undefined,
  error: undefined,
  log: '',
  lastLogSeq: -1,
  hasGap: false,
};

/** Fold one event into the accumulated state. Pure, so it is trivially testable. */
export function applyRunbookRunEvent(
  prev: RunbookRunStreamState,
  event: RunbookRunEvent
): RunbookRunStreamState {
  switch (event.type) {
    case 'runbook-run.status':
      return {
        ...prev,
        status: event.data.status,
        exitCode: event.data.exitCode,
        error: event.data.error,
      };
    case 'runbook-run.log': {
      // Already applied — a replayed event after a reconnect. Dropping it is
      // what keeps the log from growing a duplicated chunk at every boundary.
      if (event.data.seq <= prev.lastLogSeq) return prev;
      // A gap means the broker's bounded event cache evicted deltas while this
      // client was disconnected. Say so in the log rather than concatenating
      // across the hole: an operator reading a deploy's output will act on it,
      // and output that silently omits its middle is worse than output that
      // admits it is incomplete. The flushed log is complete once terminal.
      const missing = prev.lastLogSeq >= 0 && event.data.seq > prev.lastLogSeq + 1;
      return {
        ...prev,
        log:
          prev.log +
          (missing ? '\n… some output was not captured by the live stream …\n' : '') +
          event.data.stdout +
          event.data.stderr,
        lastLogSeq: event.data.seq,
        hasGap: prev.hasGap || missing,
      };
    }
  }
}

/**
 * Subscribe to a run's progress stream.
 *
 * @param runId the RUN id (not a job id); pass null to stay disconnected.
 */
export function useRunbookRunStream(runId: string | null): RunbookRunStreamState {
  const [state, setState] = React.useState<RunbookRunStreamState>(EMPTY);

  React.useEffect(() => {
    if (!runId) {
      setState(EMPTY);
      return;
    }

    setState(EMPTY);
    let cancelled = false;
    let source: EventSource | null = null;
    let reconnectTimer: number | null = null;
    let attempts = 0;
    // Replay cursor. The server drops cached events older than this, so it
    // bounds how much is re-sent — it does NOT make replay exact: the filter is
    // `emittedAt >= since`, so the event at the cursor comes back, and
    // millisecond resolution means several may share it. `seq` in the reducer,
    // not this cursor, is what makes log folds safe to repeat.
    let since: string | null = null;

    const connect = () => {
      if (cancelled) return;
      const url = new URL(`${API_URL}/jobs/${runId}/stream`);
      if (since) url.searchParams.set('since', since);
      const es = new EventSource(url.toString(), { withCredentials: true });
      source = es;

      es.onopen = () => {
        attempts = 0;
      };

      const handle = (raw: MessageEvent, type: RunbookRunEvent['type']) => {
        try {
          const event = { type, data: JSON.parse(raw.data) } as RunbookRunEvent;
          if (typeof event.data.emittedAt === 'string') since = event.data.emittedAt;
          setState((prev) => applyRunbookRunEvent(prev, event));
        } catch (error) {
          console.error('Failed to parse runbook run event', error);
        }
      };

      for (const type of RUNBOOK_RUN_EVENT_TYPES) {
        es.addEventListener(type, ((raw: MessageEvent) => handle(raw, type)) as EventListener);
      }

      es.onerror = () => {
        es.close();
        if (cancelled) return;
        attempts += 1;
        // Give up quietly rather than reconnecting forever. Losing the live
        // stream costs nothing but real-time updates — the finished log is
        // still fetched from `logUrl` once the run reaches a terminal status.
        if (attempts > MAX_RECONNECT_ATTEMPTS) return;
        const backoffMs = Math.min(10_000, 1000 * 2 ** (attempts - 1));
        reconnectTimer = window.setTimeout(connect, backoffMs);
      };
    };

    connect();

    return () => {
      cancelled = true;
      source?.close();
      if (reconnectTimer !== null) window.clearTimeout(reconnectTimer);
    };
  }, [runId]);

  return state;
}
